import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { Loader } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import { Tournament } from "../../../../../gql/graphql";
import { CreationForm } from "../../../components/form/CreationForm";
import { PageHeader } from "../../../components/PageHeader";

export const UpdateTournamentRegistrations = () => {
  const tournamentId = useParams().tournamentId;
  const { loading, data } = useQuery<{ tournament: Tournament }>(
    gql`
      query ($id: ID!) {
        tournament(id: $id) {
          startRegistrationsAt
          endRegistrationsAt
          configuration {
            teamMinSize
            teamMaxSize
            teamsCount
            teamStatusAfterRegistration
          }
        }
      }
    `,
    { variables: { id: tournamentId }, skip: !tournamentId },
  );
  const [updateTournament] = useMutation<any>(gql`
    mutation (
      $id: ID!
      $configuration: UpdateTournamentConfigurationOrImportFromIdInput!
      $startRegistrationsAt: DateTime!
      $endRegistrationsAt: DateTime!
    ) {
      updateTournament(
        id: $id
        input: {
          startRegistrationsAt: $startRegistrationsAt
          endRegistrationsAt: $endRegistrationsAt
          configuration: $configuration
        }
      ) {
        id
      }
    }
  `);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Registration Settings"
        showBackButton
        showBreadcrumbs={false}
      />
      <CreationForm
        title="Update tournament registrations settings"
        schemaDefinition={{
          startRegistrationsAt: {
            type: "INPUT",
            label: "Registrations open at",
            yupConfig: yup.date().required(),
            inputType: "date",
            placeholder: "Enter the tournament registrations open date",
            defaultValue: data?.tournament?.startRegistrationsAt,
          },
          endRegistrationsAt: {
            type: "INPUT",
            label: "Registrations closes at",
            yupConfig: yup.date().required(),
            inputType: "date",
            placeholder: "Enter the tournament registrations ending date",
            defaultValue: data?.tournament?.endRegistrationsAt,
          },
          minTeamSize: {
            type: "INPUT",
            label: "Min team size",
            yupConfig: yup.number().required(),
            inputType: "number",
            placeholder: "Enter the tournament min team size",
            defaultValue: data?.tournament?.configuration?.teamMinSize,
          },
          maxTeamSize: {
            type: "INPUT",
            label: "Max team size",
            yupConfig: yup.number().required(),
            inputType: "number",
            placeholder: "Enter the tournament max team size",
            defaultValue: data?.tournament?.configuration?.teamMaxSize,
          },
          teamsCount: {
            type: "INPUT",
            label: "Teams count",
            yupConfig: yup.number().required(),
            inputType: "number",
            placeholder: "Enter the tournament teams count",
            defaultValue: data?.tournament?.configuration?.teamsCount,
          },
          teamStatusAfterRegistration: {
            type: "SELECT",
            label: "Team status after registration",
            yupConfig: yup.string().required(),
            options: [
              { label: "Registered", value: "REGISTERED" },
              {
                label: "Awaiting for presence confirmation",
                value: "AWAITING_FOR_PRESENCE_CONFIRMATION",
              },
            ],
            defaultValue:
              data?.tournament?.configuration?.teamStatusAfterRegistration,
          },
        }}
        onSubmit={async (values) => {
          await updateTournament({
            variables: {
              id: tournamentId,
              startRegistrationsAt: values.startRegistrationsAt,
              endRegistrationsAt: values.endRegistrationsAt,
              configuration: {
                configuration: {
                  teamMinSize: values.minTeamSize,
                  teamMaxSize: values.maxTeamSize,
                  teamsCount: values.teamsCount,
                  teamStatusAfterRegistration:
                    values.teamStatusAfterRegistration,
                },
              },
            },
          }).then((data) => {
            if (data?.error) {
              throw new Error(data.error.message);
            }
          });
          notifications.show({
            title: "Tournament updated",
            message: "The tournament has been updated",
            color: "green",
            autoClose: 3000,
          });
        }}
      />
    </div>
  );
};
